import type { TenderIssue } from '@shared/api';
import { fmtNum } from '@shared/lib/smetaLabels';

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

/** Терминальные статусы — расчёт завершён, поллинг можно гасить. */
export const TERMINAL = new Set(['completed', 'failed']);

export function statusTone(status: string): Tone {
  switch (status) {
    case 'completed':
      return 'success';
    case 'parsing':
    case 'calculating':
      return 'info';
    case 'pending':
    case 'parsed':
    case 'needs_code':
      return 'warning';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function statusLabel(status: string): string {
  switch (status) {
    case 'completed':
      return 'готово';
    case 'parsing':
      return 'разбор…';
    case 'calculating':
      return 'расчёт…';
    case 'pending':
      return 'в очереди';
    // Файл разобран, позиции уже видны, но расчёт ещё не начинался. Без
    // подписи статус печатался латинским «parsed», и заполненный список
    // позиций без цен читался как «подбор ничего не нашёл».
    case 'parsed':
      return 'разобрано, расчёт в очереди';
    case 'needs_code':
      return 'нужен шифр';
    case 'failed':
      return 'ошибка';
    default:
      return status;
  }
}

export function calcModeLabel(mode: string | undefined): string {
  // «Только работы» не означает «без всего»: отдел уточнил, что машины и
  // перевозки в этом режиме считаются — исключаются лишь материалы.
  return mode === 'works_only' ? 'только работы (без материалов)' : 'работы и материалы';
}

/** Причина, по которой позиция выведена из «Итого» — словами сметчика. */
export function exclusionLabel(reason: string | null): string {
  switch (reason) {
    case 'customer_supply':
      return 'поставка Заказчика';
    case 'out_of_scope':
      return 'вне нашего договора';
    case 'other':
      return 'не считаем';
    default:
      return 'выведено из расчёта';
  }
}

export function inputKindLabel(kind: string): string {
  switch (kind) {
    case 'grand_smeta_skeleton':
      return 'Скелет Гранд-Сметы';
    case 'vor':
      return 'Ведомость объёмов (ВОР)';
    case 'volume_check':
      return 'Объёмы из Функции 1';
    case 'manual':
      return 'Ручной ввод';
    default:
      return kind;
  }
}

export function issueTone(sev: string): Tone {
  switch (sev) {
    case 'error':
      return 'danger';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'neutral';
  }
}

export function issueKindLabel(kind: string): string {
  switch (kind) {
    case 'code_not_found':
      return 'расценка не найдена';
    case 'code_outdated':
      return 'устар. шифр';
    case 'unit_mismatch':
      return 'единицы';
    case 'norm_incomplete':
      return 'неполная норма';
    case 'price_unindexed':
      return 'в базисе, без индекса';
    case 'nonpositive_qty':
      return 'объём';
    case 'duplicate_code':
      return 'дубль';
    case 'missing_code':
      return 'нет шифра';
    case 'nr_sp_missing':
      return 'нет НР/СП';
    case 'over_norm':
      return 'выше нормы';
    case 'arithmetic':
      return 'арифметика';
    case 'uncached_formulas':
      return 'формулы без значений';
    case 'nr_territory_fallback':
      return 'нет северной ставки НР';
    case 'nr_territory_unset':
      return 'категория местности';
    default:
      return kind;
  }
}

export function suggestionText(i: TenderIssue): string {
  const parts: string[] = [];
  if (i.suggested_code) parts.push(`шифр: ${i.suggested_code}`);
  if (i.suggested_unit) parts.push(`ед.: ${i.suggested_unit}`);
  if (i.suggested_qty !== null && i.suggested_qty !== undefined)
    parts.push(`кол-во: ${fmtNum(i.suggested_qty)}`);
  return parts.join('; ') || '—';
}

/** Откуда взялся шифр позиции — сметчику важно отличать свой выбор от авто. */
export function codeSourceLabel(source: string | null): string | null {
  switch (source) {
    case 'manual':
      return 'введён вручную';
    case 'accepted':
      return 'подсказка принята';
    default:
      return null;
  }
}
